import {
  Answer,
  CategoryType,
  CompensationType,
  Currency,
  CurrencySymbols,
  EducationTypes,
  EmploymentType,
  Genders,
  InterfaceLanguage,
  Languages,
  MonetizationTariff,
  ProductType,
  PurchaseType,
  SalesBanners,
  ScheduleType,
  SearchIn,
  VacancyLookType,
  WorkExperience,
} from 'types/enums';
import { ILanguageKnowledge } from './IJob';
import { ProfileType } from './IRegistration';
import { IUser } from './IUser';
import { PromoCodeResponseDto } from '@arbihunter/api-client';

export interface TimeStamp {
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  publishedAt?: string | null;
  archivedAt?: string | null;
  topAt?: string | null;
}

export interface TimeLine {
  toDate?: string;
  fromDate?: string;
  date?: string;
  toPresent?: boolean;
}

export interface IPosition {
  position: string;
}

export interface ISource extends TimeStamp {
  id: number;
  title: string;
}

export interface ICategorySearchParams {
  withVacanciesCount: boolean;
  key: 'sub-other' | 'other';
  withCVsCount: boolean;
  withNewVacanciesCount: boolean;
  withNewCVsCount: boolean;
  intervalInDays: boolean;
  search: string;
  page: number;
  limit: number;
  recommended?: boolean;
}

export interface ExperienceInfo extends TimeLine {
  // id: string | number;
  company: string;
  position: string;
  description: string;
}

export interface EducationInfo extends TimeLine {
  institution: string;
  speciality: string;
  faculty: string;
}

export interface CoursesInfo extends TimeLine {
  location: string;
  name: string;
}

export interface ITranslation extends TimeStamp {
  id: number;
  languageCode: InterfaceLanguage;
  name: string;
  description: string | null;
  baseId: number;
}

export interface IFocalPoint {
  x: number;
  y: number;
}

export interface IFile extends TimeStamp {
  id: number;
  published?: boolean;
  name?: string;
  type?: 'IMAGE' | 'VIDEO' | 'BINARY';
  mimeType?: 'image/png' | 'video/mp4' | 'application/pdf';
  fileSize?: number;
  duration?: number | null;
  source: string;
  focalPoint?: IFocalPoint;
  profileId?: number | null;
}

export interface IVideo extends TimeStamp {
  duration: number;
  fileSize: number;
  focalPoint: any | null;
  id: number;
  mimeType: 'video/mp4';
  name: string;
  published: boolean;
  source: string;
  type: 'VIDEO';
  profileId: number | null;
}

// export interface IChat extends  TimeStamp {
//   id: number,
//   type: 'chat',
//   name: string|null,
//   isGroup: boolean,
//   profileId: number,
//   profile: IUserProfile,
//   participantId: number,
//   participant: IUserProfile,
//   applicationId: number,
//   proposalId: number|null,
//   proposal: IApplication
//   last: IApplication
//   lastMessageAt: string,
// }

export interface ISpecializationCategory extends TimeStamp {
  id: number;
  imageId: number | null;
  showOnMain: boolean;
  color: number | null;
  importId: number | null;
  parentId: number | null;
  sort: number;
  image: string | null;
  translations: ITranslation[];
  languageCode: Languages;
  name: string;
  description: string | null;
  baseId: number;
  key: null | 'sub-other';
  isMain: boolean;
  vacanciesCount?: number | null;
  cvsCount?: number | null;
  newVacanciesCount?: number | null;
  newCvsCount?: number | null;
  minVacanciesSalary?: number | null;
  maxVacanciesSalary?: number | null;
  minCVsSalary?: number | null;
  maxCVsSalary?: number | null;
  withGeos?: boolean;
  withSources?: boolean;
  recommended?: boolean;
}

export interface CategoryTranslation extends TimeStamp {
  id?: number;
  languageCode: Languages;
  name?: string;
  baseId: number;
  priceDescription: string;
  shortDescription: string;
  fullDescription: string;
  audience: string;
  courseTime: string;
  requirements: string;
}

export interface ICategory
  extends TimeStamp,
    Pick<CategoryTranslation, 'id' | 'baseId' | 'languageCode' | 'name'> {
  categoryType: CategoryType;
  published: boolean;
  isMain: boolean;
  sort: string | null;
  description: string | null;
  color: string | null;
  imageId: number | null;
  articlesCount?: string;
  deletedAt: string | null;
  image?: IFile | null;
  translations: CategoryTranslation[];
  subscribers: number[];
  isSubscribedByCurrentProfile: boolean;
  coursesCount?: string | number;
  parentId?: number;
}

export interface UpdateCommunityForm {
  name: string;
  categoryId?: number;
  imageId?: number;
  logoId?: number;
}

export interface PreviewImage {
  image?: Blob;
  source?: string;
  loaded: boolean;
  id: string | number;
  rawId?: string;
  data?: IFile;
  mimeType?: string;
}

export interface IFileDownload {
  id: string;
  name: string;
  source: string;
  type: 'IMAGE' | 'BINARY';
  fileSize: number;
  focalPoint?: IFocalPoint;
  authorName?: string;
  metadata: {
    width?: number;
    height?: number;
  };
}

export interface IArticleBlock {
  id?: number;
  title?: string;
  body?: string | TrustedHTML;
  image?: Partial<IFile>;
  images?: Partial<IFile>[];
  imageLarge?: Partial<IFile>;
  imagesIds?: number[];
}

export interface ICreateNews {
  id?: number;
  communityId: number;
  articleType: CategoryType;
  name: string;
  published: boolean;
  image: { id: number };
  imageIds: number[];
  blocks: IArticleBlock[];
}

export interface IPaginationRequest {
  page?: number;
  limit?: number;
}

export interface IVacancyCvSearchParams {
  search?: string;
  keywords?: string;
  searchCompany?: string;
  searchIn?: SearchIn;
  categories?: number[];
  geos?: number[];
  verticals?: number[];
  sources?: number[];
  cities?: number[];
  countries?: (string | number)[];
  employment?: EmploymentType[];
  schedule?: ScheduleType[];
  experience?: WorkExperience[];
  currency?: Currency;
  salaryMin?: number | string;
  salaryMax?: number | string;
  salary?: number | string;
  commissionMin?: number;
  commissionMax?: number;
  gybridCommissionMin?: number;
  gybridCommissionMax?: number;
  skills?: number[]; // '1,2,3'
  isRemote?: boolean;
  savedByCurrentProfile?: boolean;
  ids?: number[]; // '1,2,3' по списку идентификаторов
  sort?: 'createdAt,DESC';
  compensation?: CompensationType;
  languageKnowledges?: ILanguageKnowledge[];
  withCharacter?: boolean;
  forLastNumberOfDays?: 1 | 7 | 30 | 365;
  s?: string;
  lookType?: VacancyLookType;
  countryIds?: number[];
  cityIds?: number[];
  education?: EducationTypes[];
  gender?: Genders[];
  vacancyLookType?: VacancyLookType[];
  relocation?: Answer[];
  ageMin?: number;
  ageMax?: number;
  exclude?: string;
}

export interface ITariff extends TimeStamp {
  baseId: number;
  colors: [string, string];
  description: string;
  icon: IFile;
  iconId: number;
  id: number;
  key: string;
  languageCode: Languages;
  name: string;
  period: number;
  price: number;
  purchaseType: PurchaseType;
  translations: ITranslation[];
  contactsCanOpenCount: number;
}

export interface ISubscription extends TimeStamp {
  cvId: number;
  vacancyId: number;
  tariff: ITariff;
  profileId: number;
  period: number;
  endAt: string;
  startAt: string;
  id: string;
  prevStatus: string | null;
  status: string | null;
  tariffId: number;
  contactsCanOpenCount: number;
}

export interface IPurchase extends TimeStamp {
  amount: number;
  characterItemId: number | null;
  id: number;
  purchaseType: PurchaseType;
  profileId: number;
  user: IUser;
  subscriptionId: number;
  subscription: ISubscription;
}

export interface RefillBalanceData {
  amount: number;
  currency: Currency;
  paymentType: 'bankCard';
}

export interface PurchaseForm {
  startAt: string | undefined;
  cvId: number | undefined;
  vacancyId: number | undefined;
  tariffId: number;
  subscriptionId: number;
}

export enum ProductStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type Benefit = {
  title: string | null;
  description: string;
  variant?: 'background' | null;
};

type MetaType = any;
export interface PurchaseProduct {
  id: number;
  code: string;
  profileType: ProfileType;
  status: ProductStatus;
  contents: {
    meta?: {
      bonusCount: number;
      bonusDiscount: number;
      discount: number;
      oldPrice: number;
      currencySymbol: CurrencySymbols;
    };
    type: ProductType;
    price: number;
    tariff: MonetizationTariff;
    totalCount: number;
    currentCount: number;
    productActiveTime: number;
    productValidityPeriod: number;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  availablePromoCode?: PromoCodeResponseDto;
}

export interface ProductsLocalizationFeature {
  text: string;
  postfix?: string;
}

export interface ProductsLocalization {
  key: string;
  title: string;
  description: string;
  features: ProductsLocalizationFeature[];
}

export enum OrderStatus {
  CREATED = 'CREATED', // Заказ создан, но еще не подтверждён
  PENDING_PAYMENT = 'PENDING_PAYMENT', // Ожидает оплаты
  PARTIAL_PAYMENT = 'PARTIAL_PAYMENT', // частично оплачен
  PAID = 'PAID', // Оплата подтверждена
  CANCELLED = 'CANCELLED', // Заказ отменён
  COMPLETED = 'COMPLETED', // Заказ выполнен (доставлен/завершён)
  REFUNDED = 'REFUNDED', // Оплата возвращена, заказ закрыт
  USE = 'USE', // Использована единица заказа
}

export interface PurchaseOrder {
  id: number;
  productId: number;
  companyId: number;
  profileId: number;
  status: OrderStatus;
  contents: {
    meta: {
      discount: number;
      oldPrice: number;
      currencySymbol: string;
    };
    type: ProductType;
    price: number;
    tariff: MonetizationTariff;
    totalCount: number;
    currentCount: number;
    productActiveTime: number;
    productValidityPeriod: number;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  activatedAt: string | null;
  paymentAmount: string;
  product: PurchaseProduct;
  paymentTransactions: null;
  isGift?: boolean;
}

export interface Deposit {
  transactionId: number;
  status: OrderStatus;
  paymentUrl: string;
  id: number;
}

export interface PaymentLink {
  id: number;
  paymentParams: {
    currency: string;
    link: string;
    options: {
      callbackUrl: string;
      redirectCallbackUrl: string;
      redirectSuccessUrl: string;
      testApiUrl: string;
    };
    symbol: string;
  };
}

export interface DepositRequest {
  platformId?: string;
  orderId: number;
  amount: string; // 10.00
  currencies: string; // 'USDT,BTC'
  symbol: string; // 'USD'
}

export interface DepositRequestTest {
  platformId?: string;
  orderId: number;
  amount: string; // 10.00
  currencies: string; // 'USDT,BTC'
  symbol: string; // 'USD'
  options: {
    // Только наша версия тестовая PassimPay требует эти поля в запросе, у PassimPay оригинального установка ссылок через личный кабинет
    redirectSuccessUrl: string; // '{{baseFrontUrl}}/success-payment'
    redirectFailedUrl: string; // '{{baseFrontUrl}}/failed-payment'
  };
}

interface PageJsonBlockTranslation {
  id: number;
  title: string;
  meta: {
    [key: string]: {
      category: {
        title: string;
      };
      benefits: {
        title: string | null;
        description: string;
        variant: 'background' | null;
      }[];
    };
  };
}

export interface PageJsonBlock {
  id: number;
  key: string;
  published: boolean;
  translations: PageJsonBlockTranslation[];
}

export interface ContactsData {
  expiredAt: string;
  telegram: string;
  email: string;
  phone: string;
}

export interface OpenContactsResponse {
  status: string;
  contacts: ContactsData;
}

export interface Banner {
  position: SalesBanners;
  profileType: ProfileType;
  mobile: {
    image: string;
    referral: string;
  };
  desktop: {
    image: string;
    referral: string;
  };
}

export interface GetBannersResponse {
  uuid?: string; // возвращается, если не был передан id или id устарел
  banners: Banner[];
}
